import { push } from 'connected-react-router'
import * as accountActions from 'redux/actions/account.actions'
import * as domainsListActions from 'redux/actions/domainsList.actions'
import * as actions from 'redux/actions/domainsData.actions'
import * as dashboardActions from 'redux/actions/dashboard.actions'
import * as keywordPagesActions from 'redux/actions/keywordPages.actions'
import * as keywordGrossingActions from 'redux/actions/keywordGrossing.actions'
import * as keywordDiscoveryActions from 'redux/actions/keywordDiscovery.actions'
import * as keywordSavingsOverviewActions from 'redux/actions/keywordSavingsOverview.actions'
import * as notifictionActions from 'redux/actions/notification.actions'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import moment from 'moment'
import agent from 'agent'
import { mocked } from 'agent'

import {
  getDomainAveragePosition,
  getDomainOverview,
  getDomainPosition,
  getDomainSerpFeatureChart,
  getDomainTraffic,
  getDomainVisibility
} from './domain.sagas'
import {
  getCompetitorsAverageRankChart,
  getCompetitorsPosition,
  getCompetitorsSerpFeatures,
  getCompetitorsSerpFeaturesChart,
  getCompetitorsTraffic,
  getCompetitorsVisibility
} from './competitors.sagas'
import getData from 'helpers/getData'
import appMode from 'helpers/getAppMode'
import NotificationToast from '_base/Notification'
import {NewKeywords, NewCompetitors, NewProject} from '_base/Processing'

function* getDomain(id, filters) {
  try {
    const body = appMode.share
      ? yield call(agent.Share.getDomain, id, filters)
      : appMode.preview
      ? yield call(mocked.Domains.get)
      : yield call(agent.Domains.get, id, filters)

    const domain = body.data

    // Do not fetch data for deleted domain
    if (domain.attributes.isDomainDeleted) {
      return {
        ...domain,
        id,
        attributes: {
          id: id,
          ...domain.attributes
        }
      }
    }

    const [
      visibilityChart,
      overview,
      averageRankChart,
      trafficChart,
      serpFeatures,
      positionsChart
    ] = yield all([
      call(getDomainVisibility, id, filters),
      call(getDomainOverview, id, filters),
      call(getDomainAveragePosition, id, filters),
      call(getDomainTraffic, id, filters),
      call(getDomainSerpFeatureChart, id, filters),
      call(getDomainPosition, id, filters)
    ])

    const data = {
      ...domain,
      id: id,
      attributes: {
        id: id,
        ...domain.attributes,
        ...overview.attributes,
        ...overview.attributes.positions,
        averageRankChart,
        serpFeatures,
        serpFeaturesChange: overview.attributes.serpFeatures,
        visibilityChart,
        trafficChart,
        positionsChart
      }
    }
    return data
  } catch (error) {
    return error
  }
}

function* getLoadDomains(loadDomain, id, filters) {
  if (!loadDomain) return null

  const state = yield select()
  yield put(actions.flagGetDomainData())

  const currentFilters = filters || state.domainsData.currentFilters
  const data = yield call(getDomain, id, currentFilters)
  yield put(actions.loadDomainSuccess(id, data))
}

function* loadDomainSaga(params) {
  try {
    const { id, filters } = params.payload
    const body = yield call(getDomain, id, filters)

    yield put(actions.loadDomainSuccess(id, body.data.attributes))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainExtendedSaga(params) {
  try {
    const { id, filters } = params.payload
    const data = yield call(getDomain, id, filters)

    yield put(actions.loadDomainSuccess(id, data))

    yield put(actions.loadKeywords(id, filters))
    yield put(actions.loadCompetitors(id, filters))

    yield put(keywordGrossingActions.getDomainKeywordGrossing(id, filters))

    yield put(keywordSavingsOverviewActions.getDomainKeywordSavingsOverview(id, filters))

    yield put(keywordDiscoveryActions.getDomainKeywordDiscovery(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainTagsSaga(params) {
  try {
    const { id } = params.payload
    const request = appMode.share
      ? agent.Share.tags
      : appMode.preview
      ? mocked.Domains.tags
      : agent.Domains.tags
    const body = yield call(request, id)

    yield put(actions.loadDomainTagsSuccess(id, body.data))
  } catch (error) {
    yield put(actions.loadDomainTagsFailure(error))
  }
}

function* loadDomainKeywordsSaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(actions.loadKeywords(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainCompetitorSaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(actions.loadKeywordsCompetitors(id, filters))
    yield put(actions.loadCompetitors(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainPagesSaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(actions.loadKeywords(id, { device: 'desktop' }))
    yield put(actions.loadCompetitors(id, { device: 'desktop' }))

    yield put(keywordPagesActions.getPages(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainSavingsSaga(params) {
  try {
    const { id, filters } = params.payload
    yield call(loadDomainPagesSaga, params)
    yield put(keywordSavingsOverviewActions.getDomainKeywordSavingsOverview(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainGrossingSaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(keywordGrossingActions.getDomainKeywordGrossing(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainDiscoverySaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(actions.loadKeywords(id, { device: 'desktop' }))
    yield put(actions.loadCompetitors(id, { device: 'desktop' }))
    yield put(keywordDiscoveryActions.getDomainKeywordDiscovery(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* loadDomainNotificationSaga(params) {
  try {
    const { id, filters, loadDomain } = params.payload
    yield call(getLoadDomains, loadDomain, id, filters)
    yield put(actions.loadKeywords(id, { device: 'desktop' }))
    yield put(actions.loadCompetitors(id, { device: 'desktop' }))
    yield put(notifictionActions.getNotificationOverview(id, filters))
  } catch (error) {
    yield put(actions.loadDomainFailure(error))
  }
}

function* addDomainSaga(params) {
  try {
    const { payload } = params.payload
    const data = yield call(agent.Domains.create, payload)
    const id = data.wizard.uuid
    yield all([
      put(accountActions.getAccountData()),
      put(domainsListActions.loadDomains()),
      put(actions.addDomainSuccess(data))
    ])

    yield put(push(`/domains/${id}`))
    yield call(NewProject, payload.domain.projectName)
  } catch (error) {
    if (error.response.status === 422) {
      NotificationToast('error', `${error.response.body.errors[0].detail}`)
    }

    yield put(actions.addDomainFailure(error))
  }
}

function* updateDomainSaga(params) {
  try {
    const { id, payload } = params.payload
    const hasCompetitors = payload.domain.competitors.length > 0
    const hasKeywords = payload.domain.keywords.words.length > 0
    yield call(agent.Domains.update, id, payload)
    yield all([
      put(
        actions.loadDomainExtended(id, {
          device: 'desktop'
        })
      ),
      put(
        dashboardActions.getDashboardData(id, {
          startDate: moment()
            .subtract(7, 'days')
            .format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD')
        })
      ),
      put(accountActions.getAccountData())
    ])

    yield put(push(`/domains/${id}`))
    if (hasKeywords) {
      yield call(NewKeywords)
    }
    if (hasCompetitors) {
      yield call(NewCompetitors)
    }
  } catch (error) {
    yield put(actions.updateDomainFailure(error))
  }
}

function* getDomainShareLink(params) {
  try {
    const { id, payload } = params.payload
    const body = yield call(agent.Domains.shareLink, id, payload)
    yield put(actions.getShareLinkSuccess(body.data))
  } catch (error) {
    yield put(actions.getShareLinkFailure(error))
  }
}

function* loadKeywordsSaga(params) {
  try {
    const { id, filters } = params.payload
    
    const [request, requestResults, requestChanges] = appMode.share
      ? [agent.Share.get, agent.Share.getResults, agent.Share.getChanges]
      : appMode.preview
      ? [mocked.Keywords.get, mocked.Keywords.getResults, mocked.Keywords.getChanges]
      : [agent.Keywords.get, agent.Keywords.getResults, agent.Keywords.getChanges]

    yield call(getData, actions.loadKeywordsSuccess, request, id, filters)
    yield all([
      call(getData, actions.loadKeywordsResultsSuccess, requestResults, id, filters),
      call(getData, actions.loadKeywordsChangesSuccess, requestChanges, id, filters)
    ])
  } catch (error) {
    yield put(actions.loadKeywordsFailure(error))
  }
}

function* loadKeywordsCompetitorsSaga(params) {
  try {
    const { id, filters } = params.payload
    
    const [
      request,
      requestResults,
      requestChanges,
      requestCompetitorsResults,
      requestCompetitorsChanges
    ] = appMode.share
      ? [
          agent.Share.get,
          agent.Share.getResults,
          agent.Share.getChanges,
          agent.Share.getCompetitorsResults,
          agent.Share.getCompetitorsChanges
        ]
      : [
          agent.Keywords.get,
          agent.Keywords.getResults,
          agent.Keywords.getChanges,
          agent.Keywords.getCompetitorsResults,
          agent.Keywords.getCompetitorsChanges
        ]

    yield call(getData, actions.loadKeywordsSuccess, request, id, filters)
    yield all([
      call(getData, actions.loadKeywordsResultsSuccess, requestResults, id, filters),
      call(getData, actions.loadKeywordsChangesSuccess, requestChanges, id, filters),
      call(
        getData,
        actions.loadKeywordsCompetitorsResultsSuccess,
        requestCompetitorsResults,
        id,
        filters
      ),
      call(
        getData,
        actions.loadKeywordsCompetitorsChangesSuccess,
        requestCompetitorsChanges,
        id,
        filters
      )
    ])
  } catch (error) {
    yield put(actions.loadKeywordsFailure(error))
  }
}

function* loadCompetitorsSaga(params) {
  try {
    const { id, filters } = params.payload

    const request = appMode.share
      ? agent.Share.Competitors.overview
      : appMode.preview
      ? mocked.Competitors.byDomain
      : agent.Competitors.byDomain

    const data = yield call(request, id, filters)
    const competitors = data.data
    const [
      visibilityChart,
      averageRankChart,
      trafficChart,
      serpFeaturesChart,
      serpFeatures,
      overviewChart
    ] = yield all([
      call(getCompetitorsVisibility, id, filters),
      call(getCompetitorsAverageRankChart, id, filters),
      call(getCompetitorsTraffic, id, filters),
      call(getCompetitorsSerpFeaturesChart, id, filters),
      call(getCompetitorsSerpFeatures, id, filters),
      call(getCompetitorsPosition, id, filters)
    ])

    const overview = {
      ...competitors.attributes,
      attributes: {
        visibilityChart,
        averageRankChart,
        trafficChart,
        serpFeaturesChart,
        overviewChart,
        overview: competitors.map(item => {
          const data = {
            id: item.id,
            ...item.attributes,
            ...item.attributes.positions,
            ...item.attributes.serpFeatures
          }
          delete data.positions
          delete data.serpFeatures
          return data
        }),
        serpFeatures
      }
    }
    yield put(actions.loadCompetitorsSuccess(id, overview))
  } catch (error) {
    yield put(actions.loadCompetitorsFailure(error))
  }
}

export default function* domainDataSagas() {
  yield all([
    takeLatest(actions.loadDomain.type, loadDomainSaga),
    takeLatest(actions.loadDomainTags.type, loadDomainTagsSaga),
    takeLatest(actions.loadDomainExtended.type, loadDomainExtendedSaga),
    takeLatest(actions.loadDomainWithCompetitor.type, loadDomainCompetitorSaga),
    takeLatest(actions.loadDomainWithPages.type, loadDomainPagesSaga),
    takeLatest(actions.loadDomainWithSavings.type, loadDomainSavingsSaga),
    takeLatest(actions.loadDomainWithKeywords.type, loadDomainKeywordsSaga),
    takeLatest(actions.loadDomainWithGrossing.type, loadDomainGrossingSaga),
    takeLatest(actions.loadDomainWithDiscovery.type, loadDomainDiscoverySaga),
    takeLatest(actions.loadDomainWithNotification.type, loadDomainNotificationSaga),
    takeLatest(actions.addDomain.type, addDomainSaga),
    takeLatest(actions.updateDomain.type, updateDomainSaga),
    takeLatest(actions.loadKeywords.type, loadKeywordsSaga),
    takeLatest(actions.loadKeywordsCompetitors.type, loadKeywordsCompetitorsSaga),
    takeLatest(actions.loadCompetitors.type, loadCompetitorsSaga),
    takeLatest(actions.getShareLink.type, getDomainShareLink)
  ])
}
